import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import { DiscardChangesProvider, useDiscardChanges } from 'context/discardChanges';
import { useProfile } from 'context/profile';
import isDeepEqual from 'lib/isDeepEqual';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexPermissions, adminPermissionsQueryKeys, PermissionDefinition } from 'services/api/admin/permissions';
import { adminShowUser, adminUpdateUser, adminUsersQueryKeys } from 'services/api/admin/users';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, H2, H3 } from 'styles/typography';
import EditedPermissionsIcon from 'svg/EditedPermissionsIcon';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByCategory(permissions: PermissionDefinition[]) {
  const groups: Record<string, PermissionDefinition[]> = {};
  for (const perm of permissions) {
    if (!perm.category) continue;
    if (!groups[perm.category]) groups[perm.category] = [];
    groups[perm.category].push(perm);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminEditPermissions() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();
  const canEdit = profile.has(PERMISSIONS.adminUserEdit);
  const disabled = !canEdit;

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: adminUsersQueryKeys.show(id!),
    queryFn: ({ signal }) =>
      adminShowUser(id!, signal, [
        'actingClient',
        'client',
        'credential',
        'enabledProviderTypes',
        'role',
        'selectedGroups',
        'selectedProvidedProviderTypes',
        'selectedManagedProviderTypes',
      ]),
    enabled: !!id,
  });

  const { data: allPermissions, isLoading: permsLoading } = useQuery({
    queryKey: adminPermissionsQueryKeys.index(),
    queryFn: ({ signal }) => adminIndexPermissions(signal),
  });

  // Local permissions state — initialised from user once loaded
  const [permissions, setPermissions] = useState<Record<string, boolean> | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialise local state when user data arrives
  const initialPermissions = user?.permissions as Record<string, boolean> | undefined;
  if (initialPermissions && permissions === null) {
    setPermissions({ ...initialPermissions });
  }

  const rolePermissions = (user?.role?.permissions ?? {}) as Record<string, boolean>;

  const dirty = useMemo(() => {
    if (!permissions || !initialPermissions) return false;
    return !isDeepEqual(permissions, initialPermissions);
  }, [permissions, initialPermissions]);

  const handleToggle = (name: string, value: boolean) => {
    setPermissions((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleDiscard = useCallback(() => {
    if (initialPermissions) setPermissions({ ...initialPermissions });
  }, [initialPermissions]);

  const handleSave = async () => {
    if (!permissions || !id) return;
    setSaving(true);
    try {
      await adminUpdateUser(id, { permissions }, 'role');
      setPermissions({ ...permissions });
      await queryClient.invalidateQueries({ queryKey: adminUsersQueryKeys.show(id) });
      addToast({ text: 'Your changes have been saved!' });
    } catch {
      addToast({ text: 'There was an error saving permissions. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (userLoading || permsLoading || !user || !allPermissions || !permissions) {
    return <CircularProgress />;
  }

  const nonAdminPerms = allPermissions.filter((p) => !p.name.startsWith('admin'));
  const adminPerms = allPermissions.filter((p) => p.name.startsWith('admin'));
  const showAdminSection = user.client?.isAdmin;

  return (
    <DiscardChangesProvider onConfirm={handleDiscard} shouldBlock={dirty}>
      <PageWrapper>
        <HeaderArea>
          <Header>
            <TitleSection>
              <Title>{user.name}</Title>
              <Subtitle>
                {user.client?.name} — {user.role?.name}
              </Subtitle>
            </TitleSection>
            <ButtonGroup>
              <DiscardButton disabled={disabled || !dirty || saving} />
              <Button onClick={handleSave} disabled={disabled || !dirty || saving} loading={saving}>
                Save Changes
              </Button>
            </ButtonGroup>
          </Header>
        </HeaderArea>
        <Content>
          <SectionsContainer>
            <PermissionSection
              label='User Permissions'
              permissions={nonAdminPerms}
              values={permissions}
              rolePermissions={rolePermissions}
              disabled={disabled}
              onToggle={handleToggle}
            />
            {showAdminSection && (
              <PermissionSection
                label='Admin Permissions'
                permissions={adminPerms}
                values={permissions}
                rolePermissions={rolePermissions}
                disabled={disabled}
                onToggle={handleToggle}
              />
            )}
          </SectionsContainer>
        </Content>
      </PageWrapper>
    </DiscardChangesProvider>
  );
}

// ---------------------------------------------------------------------------
// PermissionSection
// ---------------------------------------------------------------------------

function PermissionSection({
  label,
  permissions,
  values,
  rolePermissions,
  disabled,
  onToggle,
}: {
  label: string;
  permissions: PermissionDefinition[];
  values: Record<string, boolean>;
  rolePermissions: Record<string, boolean>;
  disabled: boolean;
  onToggle: (name: string, value: boolean) => void;
}) {
  const grouped = useMemo(() => groupByCategory(permissions), [permissions]);

  return (
    <SectionBox>
      <SectionLabel>{label}</SectionLabel>
      {Object.entries(grouped).map(([category, perms]) => (
        <CategoryGroup key={category}>
          <CategoryHeader>{category}</CategoryHeader>
          {perms.map((perm) => {
            const isModified = !!values[perm.name] !== !!rolePermissions[perm.name];
            return (
              <CheckboxRow key={perm.name}>
                <Checkbox
                  checked={!!values[perm.name]}
                  onChange={(val) => onToggle(perm.name, val)}
                  disabled={disabled}
                  label={perm.label}
                  labelSize='12px'
                  size={14}
                  color={colors.black}
                  checkedColor={colors.primaryBlue}
                />
                {isModified && (
                  <ModifiedBadge>
                    <EditedPermissionsIcon width={12} height={12} ovalFill={colors.accentYellow} />
                    <ModifiedText>Modified</ModifiedText>
                  </ModifiedBadge>
                )}
              </CheckboxRow>
            );
          })}
        </CategoryGroup>
      ))}
    </SectionBox>
  );
}

// ---------------------------------------------------------------------------
// DiscardButton — must be a child of DiscardChangesProvider to access context
// ---------------------------------------------------------------------------

function DiscardButton({ disabled }: { disabled: boolean }) {
  const { openDiscardChangesModal } = useDiscardChanges();
  return (
    <Button variant='ghost' onClick={() => openDiscardChangesModal({ navigatingAway: false })} disabled={disabled}>
      Discard Changes
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const HeaderArea = styled.div`
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 24px;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled(H2)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-size: 16px;
  margin-top: 12px;
`;

const Content = styled.div`
  padding: 24px;
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const SectionBox = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 5px;
  padding: 24px;
  width: 500px;
  align-self: flex-start;
`;

const SectionLabel = styled(H3)`
  margin-bottom: 8px;
`;

const CategoryGroup = styled.div`
  &:first-of-type {
    margin-top: 16px;
  }
  margin-top: 24px;
`;

const CategoryHeader = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: 700;
  margin-bottom: 8px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
`;

const ModifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => theme.colors.accentYellow};
  border-radius: 12px;
  padding: 2px 7px;
`;

const ModifiedText = styled.span`
  font-size: 8px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
`;
